import { AspectRatioValue } from '@/components/AspectRatioDropdown';
import { toast } from '@/components/toast';
import { useTiming } from '@/hooks/useTiming';
import { useCreateProject } from '@/pages/AdvancedEditor/hooks/useCreateProject';
import { newProjectId, SIDEBAR_PANELS } from '@/pages/AdvancedEditor/types';
import { useAnalytics } from '@/services/analytics';
import { useAuth } from '@/services/auth';
import { useIdb } from '@/services/idb';
import { logger } from '@/services/logger';
import { DEFAULT_VIDEO_NAME } from '@/state/presenters/presenters';
import { AppRoutes, MixPanelEvent } from '@/utils/types';
import { IProjectTemplate, Presenter } from '@d-id/types';
import { IDBPDatabase } from 'idb';
import { StoreType } from 'polotno/model/store';
import { useCallback, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

async function getVideoName(idb: IDBPDatabase<any> | null, from?: string | null) {
    if (!from) return DEFAULT_VIDEO_NAME;
    const draft = await idb?.get('v2drafts', newProjectId);
    return draft?.videoName ?? DEFAULT_VIDEO_NAME;
}

interface IOptions {
    isEditAsNew?: boolean;
    loadWalkthrough?: boolean;
    initialAvatar?: Presenter;
    from?: 'Home' | 'Videos' | 'Avatars';
    template?: StoreType;
    initPanel?: SIDEBAR_PANELS;
    selectedTemplate?: IProjectTemplate;
    selectedTemplateIndex?: number;
    aspectRatio?: AspectRatioValue;
    isNewProject?: boolean;
    mixPanelFrom?: string;
}

interface Props {
    projectId?: string;
    from?: string | null;
    options?: IOptions;
    mixPanelFrom?: string;
}

export const getBackRoute = (from?: string) => {
    if (from === 'Avatars') return AppRoutes.Avatars;
    if (from === 'Home') return AppRoutes.Home;
    return AppRoutes.VideoLibrary;
};

export const useNavigateToAdvancedEditor = () => {
    const idb = useIdb();
    const { isAnonymous, user } = useAuth();
    const { mutateAsync: createProject } = useCreateProject();
    const [isLoading, setIsLoading] = useState(false);
    const mixpanel = useAnalytics();
    const { projectId: paramsProjectId } = useParams();
    const { trace, withTiming } = useTiming();

    const navigate = useNavigate();

    const navigateToAdvancedEditor = useCallback(
        async (props?: Props) => {
            const { projectId, from, options, mixPanelFrom } = props || {};

            setIsLoading(true);
            trace('navigateToAdvancedEditor', { message: 'enter' });

            const isNewProjectAndLoggedIn = projectId === newProjectId && !isAnonymous;

            try {
                if (projectId && !isNewProjectAndLoggedIn) {
                    navigate(`${AppRoutes.AdvancedEditor}/${projectId}`, {
                        state: options,
                    });
                    return;
                }

                if (isAnonymous) {
                    navigate(`${AppRoutes.AdvancedEditor}/${newProjectId}`, {
                        state: options,
                    });
                } else {
                    const videoName = await getVideoName(idb, from);
                    const { id: newlyCreatedProjectId } = await withTiming('createProject', () => createProject(videoName));

                    if (mixPanelFrom) {
                        mixpanel?.track('create-video', { event: MixPanelEvent.CLICK, from: mixPanelFrom, videoId: newlyCreatedProjectId });
                    }

                    navigate(`${AppRoutes.AdvancedEditor}/${newlyCreatedProjectId}`, {
                        state: {
                            ...options,
                            fromIdb: paramsProjectId === newProjectId || projectId === newProjectId || from === 'advanced',
                            isNewProject: true,
                            mixPanelFrom,
                        },
                    });
                }
            } catch (e) {
                logger.error('Failed to create project', { error: e, user });
                console.error(e);
                toast.error({ title: 'Failed to create project' });
            }

            setIsLoading(false);
        },
        [createProject, idb, isAnonymous, mixpanel, navigate, paramsProjectId, trace, withTiming, user]
    );

    return { navigateToAdvancedEditor, isLoading };
};
